import React, { Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Layout from "../layout/Layout";
import AuthGuard from "../guards/AuthGuard";
import LoadingSpinner from "../components/LoadingSpinner";
import ScrollToTop from "../utilities/scrollToTop";

// Lazy load components for better performance
const Home = React.lazy(() => import("../pages/Home"));
const Login = React.lazy(() => import("../pages/Login"));
const ForgotPassword = React.lazy(() => import("../pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("../pages/ResetPassword"));
const VerifyEmail = React.lazy(() => import("../pages/VerifyEmail"));
const Signup = React.lazy(() => import("../pages/Signup"));
const Destinations = React.lazy(() => import("../pages/Destinations"));
const RetreatTypes = React.lazy(() => import("../pages/RetreatTypes"));
const Offers = React.lazy(() => import("../pages/Offers"));
const Blog = React.lazy(() => import("../pages/Blog"));
const AboutUs = React.lazy(() => import("../pages/AboutUs"));
const ContactUs = React.lazy(() => import("../pages/ContactUs"));
const SupportUs = React.lazy(() => import("../pages/SupportUs"));
const JoinAsPartner = React.lazy(() => import("../pages/JoinAsPartner"));
const PartnerRegistration = React.lazy(
  () => import("../pages/PartnerRegistration")
);
const RetreatDetail = React.lazy(() => import("../pages/RetreatDetail"));
const BlogPost = React.lazy(() => import("../pages/BlogPost"));
const Profile = React.lazy(() => import("../pages/Profile"));
const Bookings = React.lazy(() => import("../pages/Bookings"));
const MyBookings = React.lazy(() => import("../pages/MyBookings"));
const Booking = React.lazy(() => import("../pages/Booking"));
const BookingConfirmation = React.lazy(
  () => import("../pages/BookingConfirmation")
);
const Reservation = React.lazy(() => import("../pages/Reservation"));
const TermsConditions = React.lazy(() => import("../pages/TermsConditions"));
const NotFound = React.lazy(() => import("../pages/NotFound"));

// Admin components
const AdminLayout = React.lazy(() => import("../admin/layout/AdminLayout"));
const Dashboard = React.lazy(() => import("../admin/pages/Dashboard"));
const UploadRetreat = React.lazy(() => import("../admin/pages/UploadRetreat"));
const UploadRetreatSteps = React.lazy(
  () => import("../admin/pages/UploadRetreatSteps")
);
const MyRetreats = React.lazy(() => import("../admin/pages/MyRetreats"));
const Payments = React.lazy(() => import("../admin/pages/Payments"));
const Accounts = React.lazy(() => import("../admin/pages/Accounts"));
const AdminAccounts = React.lazy(() => import("../admin/pages/AdminAccounts"));
const Drafts = React.lazy(() => import("../admin/pages/Drafts"));
const PendingRetreats = React.lazy(
  () => import("../admin/pages/PendingRetreats")
);
const AllRetreats = React.lazy(() => import("../admin/pages/AllRetreats"));
const PublishedRetreats = React.lazy(
  () => import("../admin/pages/PublishedRetreats")
);
const UsersPartners = React.lazy(() => import("../admin/pages/UsersPartners"));
const BlogManagement = React.lazy(
  () => import("../admin/pages/BlogManagement")
);
const CreateBlog = React.lazy(() => import("../admin/pages/CreateBlog"));
const EditBlog = React.lazy(() => import("../admin/pages/EditBlog"));
const DestinationManagement = React.lazy(
  () => import("../admin/pages/DestinationManagement")
);
const RetreatPreview = React.lazy(
  () => import("../admin/pages/RetreatPreview")
);
const AdminRetreatPreview = React.lazy(
  () => import("../admin/pages/AdminRetreatPreview")
);

// Role Guard
const RoleGuard = React.lazy(() => import("../guards/RoleGuard"));

const AppRoutes: React.FC = () => {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route
          path="/"
          element={
            <Layout>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <Home />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/destinations"
          element={
            <Layout>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <Destinations />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/retreat-types"
          element={
            <Layout>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RetreatTypes />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/offers"
          element={
            <Layout>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <Offers />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/blog"
          element={
            <Layout>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <Blog />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/blog/:id"
          element={
            <Layout>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <BlogPost />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/about"
          element={
            <Layout>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <AboutUs />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/contact"
          element={
            <Layout>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <ContactUs />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/support"
          element={
            <Layout>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <SupportUs />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/terms-conditions"
          element={
            <Layout>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <TermsConditions />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/retreats/:id"
          element={
            <Layout>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RetreatDetail />
              </Suspense>
            </Layout>
          }
        />

        {/* Auth Routes - redirect if already authenticated */}
        <Route
          path="/login"
          element={
            <AuthGuard requireAuth={false} redirectTo="/">
              <Layout showNavbar={false} showFooter={false}>
                <Suspense fallback={<LoadingSpinner fullScreen />}>
                  <Login />
                </Suspense>
              </Layout>
            </AuthGuard>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <AuthGuard requireAuth={false} redirectTo="/">
              <Layout showNavbar={false} showFooter={false}>
                <Suspense fallback={<LoadingSpinner fullScreen />}>
                  <ForgotPassword />
                </Suspense>
              </Layout>
            </AuthGuard>
          }
        />

        <Route
          path="/reset-password"
          element={
            <AuthGuard requireAuth={false} redirectTo="/">
              <Layout showNavbar={false} showFooter={false}>
                <Suspense fallback={<LoadingSpinner fullScreen />}>
                  <ResetPassword />
                </Suspense>
              </Layout>
            </AuthGuard>
          }
        />

        <Route
          path="/signup"
          element={
            <AuthGuard requireAuth={false} redirectTo="/">
              <Layout showNavbar={false} showFooter={false}>
                <Suspense fallback={<LoadingSpinner fullScreen />}>
                  <Signup />
                </Suspense>
              </Layout>
            </AuthGuard>
          }
        />

        <Route
          path="/verify-email"
          element={
            <AuthGuard requireAuth={false} redirectTo="/">
              <Layout showNavbar={false} showFooter={false}>
                <Suspense fallback={<LoadingSpinner fullScreen />}>
                  <VerifyEmail />
                </Suspense>
              </Layout>
            </AuthGuard>
          }
        />

        {/* Protected Routes - require authentication */}
        <Route
          path="/profile"
          element={
            <AuthGuard requireAuth={true}>
              <Layout>
                <Suspense fallback={<LoadingSpinner fullScreen />}>
                  <Profile />
                </Suspense>
              </Layout>
            </AuthGuard>
          }
        />

        <Route
          path="/bookings"
          element={
            <AuthGuard requireAuth={true}>
              <Layout>
                <Suspense fallback={<LoadingSpinner fullScreen />}>
                  <Bookings />
                </Suspense>
              </Layout>
            </AuthGuard>
          }
        />

        <Route
          path="/my-bookings"
          element={
            <AuthGuard requireAuth={true}>
              <Layout>
                <Suspense fallback={<LoadingSpinner fullScreen />}>
                  <MyBookings />
                </Suspense>
              </Layout>
            </AuthGuard>
          }
        />

        <Route
          path="/booking"
          element={
            <AuthGuard requireAuth={true}>
              <Layout>
                <Suspense fallback={<LoadingSpinner fullScreen />}>
                  <Booking />
                </Suspense>
              </Layout>
            </AuthGuard>
          }
        />

        <Route
          path="/booking/confirmation"
          element={
            <Layout showNavbar={false} showFooter={false}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <BookingConfirmation />
              </Suspense>
            </Layout>
          }
        />

        {/* Additional route for payment redirects that might not include the exact path */}
        <Route
          path="/confirmation"
          element={
            <Layout showNavbar={false} showFooter={false}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <BookingConfirmation />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/reservation"
          element={
            <Layout>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <Reservation />
              </Suspense>
            </Layout>
          }
        />

        {/* Public but can be enhanced with auth */}
        <Route
          path="/join-as-partner"
          element={
            <Layout>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <JoinAsPartner />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/partner-registration"
          element={
            <Layout>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <PartnerRegistration />
              </Suspense>
            </Layout>
          }
        />

        {/* Admin Routes - Role-based Access */}
        <Route
          path="/admin/dashboard"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["admin", "partner"]}>
                  <AdminLayout>
                    <Dashboard />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        {/* Admin-only routes */}
        <Route
          path="/admin/all-retreats"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["admin"]}>
                  <AdminLayout>
                    <AllRetreats />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/admin/pending-retreats"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["admin"]}>
                  <AdminLayout>
                    <PendingRetreats />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/admin/published-retreats"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["admin"]}>
                  <AdminLayout>
                    <PublishedRetreats />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/admin/users-partners"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["admin"]}>
                  <AdminLayout>
                    <UsersPartners />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/admin/blog-management"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["admin"]}>
                  <AdminLayout>
                    <BlogManagement />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/admin/create-blog"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["admin"]}>
                  <AdminLayout>
                    <CreateBlog />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/admin/edit-blog/:id"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["admin"]}>
                  <AdminLayout>
                    <EditBlog />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/admin/destinations"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["admin"]}>
                  <AdminLayout>
                    <DestinationManagement />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        {/* Partner routes (now distinct from admin) */}
        <Route
          path="/admin/upload-retreat"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["partner"]}>
                  <AdminLayout>
                    <UploadRetreat />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/admin/upload-retreat-steps"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["partner", "admin"]}>
                  <AdminLayout>
                    <UploadRetreatSteps />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/admin/my-retreats"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["partner"]}>
                  <AdminLayout>
                    <MyRetreats />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        {/* Shared admin/partner routes */}
        <Route
          path="/admin/payments"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["admin", "partner"]}>
                  <AdminLayout>
                    <Payments />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/admin/accounts"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["admin"]}>
                  <AdminLayout>
                    <AdminAccounts />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/partner/accounts"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["partner"]}>
                  <AdminLayout>
                    <Accounts />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/partner/drafts"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["partner"]}>
                  <AdminLayout>
                    <Drafts />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/admin"
          element={<Navigate to="/admin/dashboard" replace />}
        />

        {/* Separate Partner Dashboard Routes */}
        <Route
          path="/partner"
          element={<Navigate to="/partner/dashboard" replace />}
        />

        <Route
          path="/partner/dashboard"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["partner"]}>
                  <AdminLayout>
                    <Dashboard />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/partner/upload-retreat"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["partner"]}>
                  <AdminLayout>
                    <UploadRetreat />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/partner/my-retreats"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["partner"]}>
                  <AdminLayout>
                    <MyRetreats />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/partner/payments"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["partner"]}>
                  <AdminLayout>
                    <Payments />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/partner/accounts"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["partner"]}>
                  <AdminLayout>
                    <Accounts />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />

        <Route
          path="/partner/drafts"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["partner"]}>
                  <AdminLayout>
                    <Drafts />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />
        <Route
          path="/partner/pending-retreats"
          element={
            <AuthGuard requireAuth={true}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RoleGuard allowedRoles={["partner"]}>
                  <AdminLayout>
                    <PendingRetreats />
                  </AdminLayout>
                </RoleGuard>
              </Suspense>
            </AuthGuard>
          }
        />
        {/* Fallback routes */}
        {/* Redirect old dashboard path */}
        <Route path="/dashboard" element={<Navigate to="/profile" replace />} />

        <Route
          path="/404"
          element={
            <Layout>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <NotFound />
              </Suspense>
            </Layout>
          }
        />

        {/* Handle payment redirects that might come with query parameters */}
        <Route
          path="/payment/success"
          element={
            <Layout showNavbar={false} showFooter={false}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <BookingConfirmation />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/payment/cancel"
          element={
            <Layout showNavbar={false} showFooter={false}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <BookingConfirmation />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/payment/callback"
          element={
            <Layout showNavbar={false} showFooter={false}>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <BookingConfirmation />
              </Suspense>
            </Layout>
          }
        />

        {/* Retreat Preview Route - Standalone without layout or auth (secured by sessionStorage key) */}
        <Route
          path="/preview/retreat"
          element={
            <Suspense fallback={<LoadingSpinner fullScreen />}>
              <RetreatPreview />
            </Suspense>
          }
        />

        {/* Admin Retreat Preview Route - For admin preview of pending retreats */}
        <Route
          path="/admin/preview/retreat"
          element={
            <Suspense fallback={<LoadingSpinner fullScreen />}>
              <AdminRetreatPreview />
            </Suspense>
          }
        />

        {/* Catch-all route - only redirect paths that don't start with known prefixes */}
        <Route
          path="*"
          element={
            <Layout>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <NotFound />
              </Suspense>
            </Layout>
          }
        />
      </Routes>
    </>
  );
};

export default AppRoutes;
